/**
 * API Client — cliente HTTP centralizado (spec §11.9)
 *
 * Toda comunicação com o backend passa por este cliente.
 * O frontend NÃO fala diretamente com a API Go: passa pelo BFF (mesma origem).
 *
 * RESPONSABILIDADES:
 *   1. Injeta o Authorization: Bearer <access_token> em cada request.
 *   2. Se o token estiver expirado (ou 401 recebido), solicita refresh single-flight.
 *   3. Propaga `traceparent` do OpenTelemetry (W3C Trace Context).
 *   4. Fila requests durante o refresh (não cancela; aguarda o novo token).
 *   5. Hard-fail (redirect para /login) se o refresh falhar (token revogado/JTI denylist).
 */

import {
  getAccessToken,
  isTokenExpired,
  refreshAccessToken,
  clearAccessToken,
} from '@/store/token-store'

// Base da API. Vazio (default) = MESMA ORIGEM: em dev, o proxy do Vite
// encaminha /v1, /bff → http://127.0.0.1:8080; em produção, o BFF serve a SPA
// e faz proxy para o Go. VITE_API_URL só é usado para apontar a um host
// distinto (exige CORS no destino — fora do contrato same-origin padrão).
const API_BASE_URL = import.meta.env.VITE_API_URL ?? ''
const BFF_REFRESH_ENDPOINT = `${API_BASE_URL}/bff/auth/refresh`

// Fila de requests aguardando refresh
type QueueItem = {
  resolve: (token: string | null) => void
  reject: (reason?: unknown) => void
}
/** Disparado quando a API recusa um admin sem autenticador (403 AUTENTICADOR_OBRIGATORIO); o gate de MFA escuta. */
export const EVENTO_AUTENTICADOR_OBRIGATORIO = 'ppm:autenticador-obrigatorio'

type ObservadorDeFalha = (status: number, url: string) => void
const _observadoresDeFalha = new Set<ObservadorDeFalha>()

/**
 * Registra quem quer saber de toda requisição que falhou (status 0 = rede).
 * É por aqui que o analytics conta o erro visto, sem o cliente importá-lo.
 */
export function aoFalharRequisicao(cb: ObservadorDeFalha): () => void {
  _observadoresDeFalha.add(cb)
  return () => _observadoresDeFalha.delete(cb)
}

function _avisarFalha(status: number, url: string): void {
  for (const cb of _observadoresDeFalha) {
    try {
      cb(status, url)
    } catch {
      // Observador nunca derruba a requisição.
    }
  }
}

let _refreshQueue: QueueItem[] = []
let _isRefreshing = false

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly traceId?: string,
    /** Segundos do header Retry-After (429/503), quando o servidor informa. */
    public readonly retryAfterSegundos?: number,
    /**
     * Campo do formulário a que o 422 se refere (`ENTRADA_INVALIDA` + `campo`),
     * quando o servidor informa. Deixa a tela pôr a mensagem EMBAIXO do campo
     * certo em vez de num aviso solto (Rede Comercial, REDE-09).
     */
    public readonly campo?: string,
    /** Cadastro existente quando o 409 é CPF_JA_ASSOCIADO. */
    public readonly associadoId?: string,
    /** Piso e pedido (percentuais "6.00") do 422 PARTICIPACAO_ABAIXO_DO_PISO. */
    public readonly pisoPct?: string,
    public readonly pedidoPct?: string,
    /** Versões do modelo quando o 409 é VARIAS_VERSOES (FIPE). */
    public readonly versoes?: Array<{ chave_ano: string; combustivel: string }>,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function lerRetryAfter(response: Response): number | undefined {
  const bruto = response.headers?.get?.('Retry-After')
  if (!bruto) return undefined
  const n = Number(bruto.trim())
  return Number.isInteger(n) && n > 0 ? n : undefined
}

interface ApiOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>
  skipAuth?: boolean
  /**
   * Não manda o usuário para o login quando a chamada falha por autenticação.
   *
   * Para chamadas OPCIONAIS, de fundo — as que enriquecem a tela mas não são
   * o motivo de ela existir. Sem isto, uma consulta acessória que responde
   * 401 arranca o consultor do formulário no meio do preenchimento e ele
   * perde o que digitou. O erro continua sendo lançado; quem chama decide.
   */
  skipAuthRedirect?: boolean
}

export async function apiRequest<T>(
  path: string,
  options: ApiOptions = {},
): Promise<T> {
  const url = `${API_BASE_URL}${path}`
  const { skipAuth = false, skipAuthRedirect = false, headers = {}, ...rest } = options

  let token: string | null = null

  if (!skipAuth) {
    token = await _getValidToken()
    if (!token) {
      if (!skipAuthRedirect) _handleAuthFailure()
      throw new ApiError(401, 'unauthorized', 'Sessão expirada. Por favor, faça login novamente.')
    }
  }

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  }

  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`
  }

  // Propagação de trace W3C (OpenTelemetry)
  const traceParent = generateTraceParent()
  if (traceParent) {
    requestHeaders['traceparent'] = traceParent
  }

  let response: Response
  try {
    response = await fetch(url, {
      ...rest,
      credentials: 'include', // para o cookie do refresh token
      headers: requestHeaders,
    })
  } catch (err) {
    // Cancelamento pedido pela própria tela (troca de rota, refetch) não é falha de rede.
    if (!(err instanceof DOMException && err.name === 'AbortError')) _avisarFalha(0, url)
    throw err
  }
  if (!response.ok) _avisarFalha(response.status, url)

  if (response.status === 401 && !skipAuth) {
    // Token revogado (JTI denylist) — hard fail
    clearAccessToken()
    if (!skipAuthRedirect) _handleAuthFailure()
    throw new ApiError(401, 'unauthorized', 'Sessão encerrada.')
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as {
      code?: string
      message?: string
      trace_id?: string
      campo?: string
      associado_id?: string
      piso_pct?: string
      pedido_pct?: string
      versoes?: Array<{ chave_ano: string; combustivel: string }>
    }
    if (!skipAuthRedirect) _encerrarSeAcessoRevogado(response.status, body.code)
    _pedirCadastroDoAutenticador(response.status, body.code)
    throw new ApiError(
      response.status,
      body.code ?? 'unknown_error',
      body.message ?? `Erro ${response.status}`,
      body.trace_id,
      lerRetryAfter(response),
      typeof body.campo === 'string' ? body.campo : undefined,
      typeof body.associado_id === 'string' ? body.associado_id : undefined,
      typeof body.piso_pct === 'string' ? body.piso_pct : undefined,
      typeof body.pedido_pct === 'string' ? body.pedido_pct : undefined,
      Array.isArray(body.versoes) ? body.versoes : undefined,
    )
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json() as Promise<T>
}

// ─── Helpers internos ─────────────────────────────────────────────────────────

/**
 * tokenParaSocket devolve um token válido para o handshake do WebSocket.
 *
 * O socket não pode usar `apiRequest`: o navegador não permite header ao abrir
 * a conexão, e a credencial vai pelo subprotocolo (ADR-0017, decisão 1
 * corrigida). Reusar a mesma função de renovação evita que o socket abra com
 * token vencido e caia logo em seguida.
 */
export async function tokenParaSocket(): Promise<string | null> {
  return _getValidToken()
}

/** Token válido para chamadas fora do `apiRequest` (download por fetch, socket). */
export const tokenAtual = tokenParaSocket

/**
 * Trata uma resposta de falha de chamada feita fora do `apiRequest` (download de
 * arquivo): 401 derruba a sessão e manda ao login; 403 `USUARIO_INATIVO` encerra
 * o acesso revogado. Reaproveita as mesmas rotinas do `apiRequest`.
 */
export function tratarFalhaDeResposta(status: number, code?: string): void {
  if (status === 401) {
    clearAccessToken()
    _handleAuthFailure()
    return
  }
  _encerrarSeAcessoRevogado(status, code)
}

async function _getValidToken(): Promise<string | null> {
  const current = getAccessToken()

  if (current && !isTokenExpired()) {
    return current
  }

  // Token expirado → refresh
  if (_isRefreshing) {
    // Aguarda em fila (single-flight)
    return new Promise<string | null>((resolve, reject) => {
      _refreshQueue.push({ resolve, reject })
    })
  }

  _isRefreshing = true

  try {
    const newToken = await refreshAccessToken(BFF_REFRESH_ENDPOINT)
    _flushQueue(newToken, null)
    return newToken
  } catch (err) {
    _flushQueue(null, err)
    return null
  } finally {
    _isRefreshing = false
  }
}

function _flushQueue(token: string | null, error: unknown): void {
  const queue = _refreshQueue
  _refreshQueue = []
  queue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })
}

// USUARIO_INATIVO chega quando alguém é desativado com o sistema aberto. Sem
// derrubar a sessão, a pessoa segue navegando entre telas vazias e as telas que
// repetem consulta batem 403 a cada poucos segundos.
function _encerrarSeAcessoRevogado(status: number, code?: string): void {
  if (status !== 403 || code !== 'USUARIO_INATIVO') return
  clearAccessToken()
  if (typeof window !== 'undefined') {
    window.location.replace('/login?motivo=acesso-encerrado')
  }
}

function _pedirCadastroDoAutenticador(status: number, code?: string): void {
  if (status !== 403 || code !== 'AUTENTICADOR_OBRIGATORIO') return
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(EVENTO_AUTENTICADOR_OBRIGATORIO))
  }
}

function _handleAuthFailure(): void {
  // Redireciona para login (sem histórico, para evitar loop)
  if (typeof window !== 'undefined') {
    window.location.replace('/login')
  }
}

function generateTraceParent(): string | null {
  // Formato W3C: 00-{trace-id}-{span-id}-01
  // Em produção usar OpenTelemetry SDK; aqui geramos um básico
  try {
    const traceId = crypto.randomUUID().replace(/-/g, '')
    const spanId = Array.from(crypto.getRandomValues(new Uint8Array(8)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
    return `00-${traceId}-${spanId}-01`
  } catch {
    return null
  }
}

/**
 * apiRequestBlob — variante do apiRequest para respostas BINÁRIAS (F3: PDF do
 * boleto via BFF same-origin). Mesma injeção de Bearer + trace + tratamento de
 * 401; erros não-2xx continuam chegando como ApiError (o backend devolve JSON
 * de erro mesmo nas rotas binárias). skipAuth (achado "fotos invisíveis") —
 * mesmo racional de ApiOptions.skipAuth no apiRequest: rotas públicas (token
 * na URL, sem sessão) não têm Bearer nenhum pra mandar; sem isso, um visitante
 * anônimo sempre cairia em "Sessão expirada" antes de a requisição sair.
 */
export async function apiRequestBlob(path: string, options: { skipAuth?: boolean } = {}): Promise<Blob> {
  const url = `${API_BASE_URL}${path}`
  const { skipAuth = false } = options
  const headers: Record<string, string> = {}

  if (!skipAuth) {
    const token = await _getValidToken()
    if (!token) {
      _handleAuthFailure()
      throw new ApiError(401, 'unauthorized', 'Sessão expirada. Por favor, faça login novamente.')
    }
    headers['Authorization'] = `Bearer ${token}`
  }
  const traceParent = generateTraceParent()
  if (traceParent) headers['traceparent'] = traceParent

  const response = await fetch(url, { credentials: 'include', headers })
  if (response.status === 401 && !skipAuth) {
    clearAccessToken()
    _handleAuthFailure()
    throw new ApiError(401, 'unauthorized', 'Sessão encerrada.')
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { code?: string; message?: string; trace_id?: string }
    throw new ApiError(response.status, body.code ?? 'unknown_error', body.message ?? `Erro ${response.status}`, body.trace_id)
  }
  return response.blob()
}

/**
 * apiRequestUpload — variante do apiRequest para envio de `multipart/form-data`
 * (Fase 3: guia de fotos de vistoria, consultor autenticado E link público sem
 * sessão — `skipAuth: true` no 2º caso, mesmo contrato de apiRequest).
 *
 * NUNCA define `Content-Type` manualmente: o `fetch` calcula o boundary do
 * multipart sozinho a partir do FormData — sobrescrever quebra o parse no
 * backend (`ParseMultipartForm` exige o boundary correto no header).
 */
export async function apiRequestUpload<T>(
  path: string,
  formData: FormData,
  options: { skipAuth?: boolean; method?: 'POST' | 'PUT'; headers?: Record<string, string> } = {},
): Promise<T> {
  const url = `${API_BASE_URL}${path}`
  const { skipAuth = false, method = 'POST', headers: extra = {} } = options

  let token: string | null = null
  if (!skipAuth) {
    token = await _getValidToken()
    if (!token) {
      _handleAuthFailure()
      throw new ApiError(401, 'unauthorized', 'Sessão expirada. Por favor, faça login novamente.')
    }
  }

  const headers: Record<string, string> = { ...extra }
  if (token) headers['Authorization'] = `Bearer ${token}`
  const traceParent = generateTraceParent()
  if (traceParent) headers['traceparent'] = traceParent

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers,
    body: formData,
  })

  if (response.status === 401 && !skipAuth) {
    clearAccessToken()
    _handleAuthFailure()
    throw new ApiError(401, 'unauthorized', 'Sessão encerrada.')
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { code?: string; message?: string; trace_id?: string }
    throw new ApiError(response.status, body.code ?? 'unknown_error', body.message ?? `Erro ${response.status}`, body.trace_id)
  }
  return response.json() as Promise<T>
}
